import { Component, ComponentInterface, Element, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { WorkbenchTheme, getDataOnFromTheme } from '../../shared/themes';
import { ListVariant } from './list.types';

/**
 * Lists help to structure textual content, making it easier to read for the user.
 *
 * @slot - List items placed within the list
 */
@Component({
  tag: 'wb-list',
  styleUrl: 'list.scss',
  shadow: true,
})
export class ListComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Variant of the list to be displayed */
  @Prop({ reflect: true })
  public variant: ListVariant = 'unordered';

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Font size of the `wb-list-item` elements */
  @Prop({ reflect: true })
  public size: 's' | 'm' | 'l' = 'm';

  /** Font size responsiveness */
  @Prop({ reflect: true })
  public static: boolean = false;

  /** Set to true to continue the numbering of another list in this list */
  @Prop({ reflect: true })
  public continueCounter: boolean = false;

  private listItems: Array<HTMLWbListItemElement>;

  @Watch('size')
  @Watch('static')
  updateListItems() {
    this.listItems.forEach(this.applyTypographySettings);
  }

  applyTypographySettings = (listItem: HTMLWbListItemElement) => {
    listItem.size = this.size;
    listItem.static = this.static;
  };

  handleSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.listItems = slot.assignedElements() as Array<HTMLWbListItemElement>;
    this.updateListItems();
  };

  render(): JSX.Element {
    return (
      <Host class="wb-list" role="list" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <slot onSlotchange={this.handleSlotChange} />
      </Host>
    );
  }
}
